import { Navigate, Route, Routes } from 'react-router-dom';
import { lazyImport } from '@/app/routes/lazyImport';
import AppShellLayout from '@/app/layouts/AppShellLayout';
import AuthLayout from '@/app/layouts/AuthLayout';
import MarketingLayout from '@/app/layouts/MarketingLayout';
import GuestRoute from '@/app/routes/GuestRoute';
import ProtectedRoute from '@/app/routes/ProtectedRoute';
import RootRedirect from '@/app/routes/RootRedirect';
import { APP_PATHS, AUTH_PATHS } from '@/app/routes/paths';
import PaymentsAvailabilityGuard from '@/features/payments/PaymentsAvailabilityGuard';

const AboutPage = lazyImport(() => import('@/pages/AboutPage'));
const NotFoundPage = lazyImport(() => import('@/pages/NotFoundPage'));
const LoginPage = lazyImport(() => import('@/pages/LoginPage'));
const RegisterPage = lazyImport(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazyImport(() => import('@/pages/ForgotPasswordPage'));
const FeedPage = lazyImport(() => import('@/pages/FeedPage'));
const ExplorePage = lazyImport(() => import('@/pages/ExplorePage'));
const HashtagFeedPage = lazyImport(() => import('@/pages/HashtagFeedPage'));
const MessagesPage = lazyImport(() => import('@/pages/MessagesPage'));
const CalendarPage = lazyImport(() => import('@/pages/CalendarPage'));
const ReportCardsPage = lazyImport(() => import('@/pages/ReportCardsPage'));
const PaymentsPage = lazyImport(() => import('@/pages/payments/PaymentsPage'));
const PaymentsCartPage = lazyImport(() => import('@/pages/payments/PaymentsCartPage'));
const PaymentsCheckoutPage = lazyImport(() => import('@/pages/payments/PaymentsCheckoutPage'));
const NotificationsPage = lazyImport(() => import('@/pages/NotificationsPage'));
const ClassPage = lazyImport(() => import('@/pages/ClassPage'));
const ProfilePage = lazyImport(() => import('@/pages/ProfilePage'));
const EditProfilePage = lazyImport(() => import('@/pages/EditProfilePage'));
const SettingsPage = lazyImport(() => import('@/pages/SettingsPage'));
const PrimePage = lazyImport(() => import('@/pages/PrimePage'));
const OrderSummaryPage = lazyImport(() => import('@/pages/OrderSummaryPage'));
const TutorSubscriptionPage = lazyImport(() => import('@/pages/TutorSubscriptionPage'));
const TutorsDirectoryPage = lazyImport(() => import('@/pages/TutorsDirectoryPage'));
const QuestionResponsesPage = lazyImport(() => import('@/pages/QuestionResponsesPage'));
const PinIconPickerPage = lazyImport(() => import('@/pages/dev/PinIconPickerPage'));
const PublicApplicationPage = lazyImport(() => import('@/pages/apply/PublicApplicationPage'));
const ApplicationResultPage = lazyImport(() => import('@/pages/apply/ApplicationResultPage'));
const ApplicationDocumentsPage = lazyImport(() => import('@/pages/apply/ApplicationDocumentsPage'));

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      <Route element={<MarketingLayout />}>
        <Route path="/about" element={<AboutPage />} />
        <Route path="/404" element={<NotFoundPage />} />
      </Route>

      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          <Route path={AUTH_PATHS.login} element={<LoginPage />} />
          <Route path={AUTH_PATHS.register} element={<RegisterPage />} />
          <Route path={AUTH_PATHS.forgotPassword} element={<ForgotPasswordPage />} />
        </Route>
      </Route>

      <Route path="/apply/:token" element={<PublicApplicationPage />} />
      <Route path="/apply/:token/payment-result" element={<ApplicationResultPage />} />
      <Route
        path="/apply/:token/documents/:documentsToken"
        element={<ApplicationDocumentsPage />}
      />

      {/* Tutor storefronts are public; transactional/social actions gate themselves. */}
      <Route element={<AppShellLayout />}>
        <Route path={APP_PATHS.tutorSubscriptions} element={<TutorsDirectoryPage />} />
        <Route path={APP_PATHS.tutorSubscriptionsEn} element={<TutorsDirectoryPage />} />
        <Route
          path={`${APP_PATHS.tutorSubscriptions}/:username`}
          element={<TutorSubscriptionPage />}
        />
        <Route
          path={`${APP_PATHS.tutorSubscriptionsEn}/:username`}
          element={<TutorSubscriptionPage />}
        />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShellLayout />}>
          <Route path={APP_PATHS.feed} element={<FeedPage />} />
          <Route path={APP_PATHS.explore} element={<ExplorePage />} />
          <Route path={`${APP_PATHS.hashtagBase}/:tag`} element={<HashtagFeedPage />} />
          <Route
            path={`${APP_PATHS.questionsBase}/:questionId/responses`}
            element={<QuestionResponsesPage />}
          />
          <Route path={APP_PATHS.messages} element={<MessagesPage />} />
          <Route path={APP_PATHS.notifications} element={<NotificationsPage />} />
          <Route path="/dev/pin-icons" element={<PinIconPickerPage />} />
          <Route path={APP_PATHS.calendar} element={<CalendarPage />} />
          <Route element={<PaymentsAvailabilityGuard />}>
            <Route path={APP_PATHS.payments} element={<PaymentsPage />} />
            <Route path={`${APP_PATHS.payments}/:paymentReference`} element={<PaymentsPage />} />
            <Route path={`${APP_PATHS.payments}/cart`} element={<PaymentsCartPage />} />
            <Route path={`${APP_PATHS.payments}/checkout`} element={<PaymentsCheckoutPage />} />
          </Route>
          <Route path={APP_PATHS.reportCards} element={<ReportCardsPage />} />
          <Route path={APP_PATHS.profile} element={<ProfilePage />} />
          <Route path={APP_PATHS.profileEdit} element={<EditProfilePage />} />
          <Route path={`${APP_PATHS.profile}/:username`} element={<ProfilePage />} />
          <Route path={`${APP_PATHS.settings}/*`} element={<SettingsPage />} />
          <Route path={APP_PATHS.prime} element={<PrimePage />} />
          <Route path={`${APP_PATHS.orderSummary}/:referenceId`} element={<OrderSummaryPage />} />
          <Route path={`${APP_PATHS.orderSummaryEn}/:referenceId`} element={<OrderSummaryPage />} />
          <Route path={APP_PATHS.classBase} element={<Navigate to={APP_PATHS.feed} replace />} />
          <Route path={`${APP_PATHS.classBase}/:classCode`} element={<ClassPage />} />
          <Route path={`${APP_PATHS.classBase}/:classCode/:tab`} element={<ClassPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
